import { theme } from 'antd';
import { BatteryIcon, LockerUsageIcon, SizeIcon } from 'assets/images/svg';
import StatusIcon from 'assets/images/svg/StatusIcon';
import { getBookingState } from 'library/helpers/bookingHelpers';
import { getDeviceSizeCategory, getLockerUsageCategory } from 'library/helpers/lockerHelpers';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectBookingsByDeviceID } from 'reduxStore/bookings/selectors';
import { selectCategoriesLockersDashboard } from 'reduxStore/categories/selectors';
import { selectDevicesByDeviceID } from 'reduxStore/devices/selectors';
import { selectLockersLogs } from 'reduxStore/lockersLogs/selectors';
import { ManageLockerDropdown } from 'zComponents/molecules';
import { Col, Collapse, Divider, Drawer, Flex, Row } from 'zui/Antd';
import { InfoTuple } from 'zui/Custom';
import { CurrentBookingsContent, EditDrawerHeader, PanelHeader } from './components';
import AccessLogsContent from './components/AccessLogsContent';

const LockerDrawer = ({ deviceID, visible, setVisible }) => {
	// Theme tokens
	const {
		token: { paddingXXL = 48, borderRadiusLG, paddingMD, marginXS, paddingLG, padding }
	} = theme.useToken();

	// Refs
	const currentBookingsRef = useRef(null);
	const accessLogsRef = useRef(null);

	// Redux state
	const device = useSelector((state) => selectDevicesByDeviceID(state, deviceID));
	const Bookings = useSelector((state) => selectBookingsByDeviceID(state, deviceID));
	const categories = useSelector(selectCategoriesLockersDashboard);
	const lockersLogs = useSelector(selectLockersLogs);

	// Get the category for this device
	const deviceCategory = useMemo(() => {
		if (!device) return null;
		return categories.find(
			(category) => category.categoryID === device.categoryID || category.categoryID === device.defaultCategoryID
		);
	}, [device, categories]);

	// Check if there are any active bookings
	const hasActiveBooking = useMemo(() => {
		return Bookings.some((booking) => getBookingState(booking) === 'Active');
	}, [Bookings]);

	// Calculate locker usage category
	const usageCategory = useMemo(() => {
		if (!device) return 'None';
		return getLockerUsageCategory(device, lockersLogs);
	}, [device, lockersLogs]);

	// Calculate device size category
	const sizeCategory = useMemo(() => {
		if (!device) return 'Not specified';
		return getDeviceSizeCategory(device);
	}, [device]);

	// Local state
	const [activeKeys, setActiveKeys] = useState(['currentBookings', 'accessLogs']);
	const [isEditingCurrentBooking, setIsEditingCurrentBooking] = useState(false);
	const [isEditingAccessLogs, setIsEditingAccessLogs] = useState(false);
	// Close drawer
	const handleOnClose = useCallback(() => {
		setVisible(false);
	}, [setVisible]);

	// Toggle panel
	const togglePanel = useCallback((key) => {
		setActiveKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
	}, []);

	// Handle collapse change
	const handleCollapseChange = useCallback(
		(keys) => {
			setActiveKeys(keys);
			if (!keys.includes('currentBookings') && isEditingCurrentBooking) {
				setIsEditingCurrentBooking(false);
			}
			if (!keys.includes('accessLogs') && isEditingAccessLogs) {
				setIsEditingAccessLogs(false);
			}
		},
		[isEditingCurrentBooking, isEditingAccessLogs]
	);

	// Memoize collapse items
	const items = useMemo(
		() => [
			{
				key: 'currentBookings',
				label: (
					<PanelHeader
						title='CURRENT BOOKINGS'
						isExpanded={activeKeys.includes('currentBookings')}
						togglePanel={() => togglePanel('currentBookings')}
					/>
				),
				children: (
					<div ref={currentBookingsRef}>
						<CurrentBookingsContent deviceID={deviceID} />
					</div>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			},
			{
				key: 'divider-1',
				label: <Divider style={{ margin: 0 }} />,
				children: null,
				collapsible: 'disabled',
				showArrow: false,
				style: { padding: 0 }
			},
			{
				key: 'accessLogs',
				label: (
					<PanelHeader
						title='ACCESS LOGS'
						isExpanded={activeKeys.includes('accessLogs')}
						togglePanel={() => togglePanel('accessLogs')}
					/>
				),
				children: (
					<div ref={accessLogsRef}>
						<AccessLogsContent deviceID={deviceID} />
					</div>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			}
		],
		[activeKeys, currentBookingsRef, accessLogsRef, marginXS, deviceID, togglePanel]
	);

	// Memoize drawer styles
	const drawerStyles = useMemo(
		() => ({
			wrapper: { margin: '30px 20px 30px 0px' },
			content: { borderRadius: borderRadiusLG },
			body: { padding: paddingXXL }
		}),
		[borderRadiusLG, paddingXXL]
	);

	return (
		<Drawer
			placement='right'
			onClose={handleOnClose}
			open={visible}
			width={871}
			closeIcon={false}
			styles={drawerStyles}
		>
			<Flex vertical>
				<EditDrawerHeader
					deviceID={device?.deviceID}
					defaultCategoryID={device?.defaultCategoryID}
					name={device?.name}
					description={deviceCategory?.name || '-'}
					handleOnCloseEdit={handleOnClose}
				/>
				<div
					style={{
						marginTop: paddingXXL
					}}
				>
					<ManageLockerDropdown deviceID={device?.deviceID} />
				</div>

				<Divider style={{ margin: `${paddingMD}px 0px ${paddingMD}px ` }} />

				<Row>
					<Col span={24}>
						<Flex vertical gap={paddingLG} style={{ padding: padding }}>
							<InfoTuple
								icon={<StatusIcon />}
								text='Status'
								value={hasActiveBooking ? 'Booked' : 'Available'}
								highlight={hasActiveBooking ? 'success' : 'error'}
							/>
							<InfoTuple
								icon={<BatteryIcon />}
								text='Battery'
								value={device?.battery ? `${device?.battery}%` : '-'}
							/>
							<InfoTuple icon={<LockerUsageIcon />} text='Locker Usage' value={usageCategory} />
							<InfoTuple icon={<SizeIcon />} text='Size' value={sizeCategory} />
						</Flex>
					</Col>
				</Row>

				<Divider style={{ margin: `${paddingMD}px 0px ${paddingMD}px` }} />

				<Collapse
					expandIcon={() => null}
					items={items}
					activeKey={activeKeys}
					onChange={handleCollapseChange}
					ghost
				/>
			</Flex>
		</Drawer>
	);
};

export default LockerDrawer;
